import { useEffect, useMemo, useState } from "react";
import engineeringMarkdown from "../content/documents/01-grok-bot-for-engineering.md?raw";
import selfImprovementMarkdown from "../content/documents/02-harness-engineering-self-improvement.md?raw";
import grokBotGuideMarkdown from "../content/documents/03-grok-bot-what-it-is.md?raw";
import documentsData from "./data/documents.json";
import sourcesData from "./data/sources.json";
import updatesData from "./data/updates.json";
import { ChapterNav } from "./components/ChapterNav";
import { DocumentLibrary, type DocumentMeta } from "./components/DocumentLibrary";
import { Footer } from "./components/Footer";
import { Hero } from "./components/Hero";
import { MarkdownArticle } from "./components/MarkdownArticle";
import { MobileReaderControls } from "./components/MobileReaderControls";
import { ReadingRail } from "./components/ReadingRail";
import { SearchDialog } from "./components/SearchDialog";
import { SiteHeader } from "./components/SiteHeader";
import { SourceLedger } from "./components/SourceLedger";
import { WeeklyUpdates } from "./components/WeeklyUpdates";
import { useReaderState } from "./hooks/useReaderState";
import { parseHeadings, prepareGuide } from "./lib/content";
import type { SourcesData, UpdatesData } from "./types/content";

const typedSources = sourcesData as SourcesData;
const typedUpdates = updatesData as UpdatesData;
const documents = documentsData as DocumentMeta[];
const markdownByDocument: Record<string, string> = {
  engineering: engineeringMarkdown,
  "self-improvement": selfImprovementMarkdown,
  "grok-bot-guide": grokBotGuideMarkdown,
};

export default function App() {
  const [activeDocumentId, setActiveDocumentId] = useState(() => {
    const id = new URLSearchParams(window.location.search).get("document");
    return documents.some((document) => document.id === id) ? id! : documents[0].id;
  });
  const activeDocument = documents.find((document) => document.id === activeDocumentId) ?? documents[0];
  const markdown = useMemo(
    () => prepareGuide(markdownByDocument[activeDocument.id]),
    [activeDocument.id],
  );
  const headings = useMemo(() => parseHeadings(markdown), [markdown]);
  const { activeHeading, progress } = useReaderState(headings);
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    const saved = window.localStorage.getItem("agent-harness-reader-theme");
    return saved === "light" ? "light" : "dark";
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("agent-harness-reader-theme", theme);
  }, [theme]);

  const selectDocument = (id: string) => {
    setActiveDocumentId(id);
    window.history.replaceState(null, "", "?document=" + encodeURIComponent(id));
    window.setTimeout(() => document.getElementById("reader")?.scrollIntoView({ behavior: "smooth" }), 0);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMobileNavOpen(false);
        setTocOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <>
      <div id="top" />
      <SiteHeader
        theme={theme}
        onToggleTheme={() => setTheme((value) => (value === "dark" ? "light" : "dark"))}
        onOpenSearch={() => setSearchOpen(true)}
        mobileNavOpen={mobileNavOpen}
        onToggleMobileNav={() => setMobileNavOpen((value) => !value)}
      />
      <main>
        <Hero />
        <DocumentLibrary documents={documents} activeId={activeDocument.id} onSelect={selectDocument} />
        <section className="active-document-heading" aria-labelledby="active-document-title">
          <div>
            <span>正在阅读 · {activeDocument.index}</span>
            <h2 id="active-document-title">{activeDocument.title}</h2>
            <p>{activeDocument.subtitle}</p>
          </div>
          <a className="secondary-button" href={`${import.meta.env.BASE_URL}${activeDocument.pdf}`} download>
            下载本篇 PDF
          </a>
        </section>
        <div className="reader-layout" id="reader">
          <ChapterNav headings={headings} activeHeading={activeHeading} />
          <MarkdownArticle markdown={markdown} />
          <ReadingRail progress={progress} updates={typedUpdates} />
        </div>
        <WeeklyUpdates updates={typedUpdates} />
        <SourceLedger
          sources={typedSources}
          sourceIds={activeDocument.sourceIds}
          referenceIds={activeDocument.referenceIds}
        />
      </main>
      <Footer />
      <MobileReaderControls
        headings={headings}
        activeHeading={activeHeading}
        progress={progress}
        tocOpen={tocOpen}
        onToggleToc={() => setTocOpen((value) => !value)}
      />
      <SearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        headings={headings}
        sources={typedSources}
      />
    </>
  );
}
